import React from 'react';
import Loader from 'react-loader-spinner';
import axios from 'axios';
import dividia_logo_white from '../images/dividia_white_blue.png';
import dividia_logo_dark from '../images/dividia_black_blue.png';
import { getTheme } from '../util/theme';
import { shouldShowLoginError } from './loginErrorState';
import SessionExistsModal from './SessionExistsModal';
import SessionExpiredModal from './SessionExpiredModal';
import MaxSessionsModal from './MaxSessionsModal';
import SessionErrorModal from './SessionErrorModal';
import PasswordExpiredModal from './PasswordExpiredModal';
import CloudPasswordChangeModal from './CloudPasswordChangeModal';
import InactivityModal from './InactivityModal';
import ServerStatusModal from '../common/ServerStatusModal';
import AlertModal from '../common/AlertModal';
import { connect } from 'react-redux';
import {
  usernameChanged,
  passwordChanged,
  loginUser,
  loginCloud,
  changePasswordCloud,
  loginMicrosoft,
  loginCloudMicrosoft,
  screenChange,
  clearSessionModal,
  loginToServer
} from '../actions';
import OauthProviderButton, { OrSeparator } from './OauthProviderButton';
import { selectServingApiToken } from '../backend/oauthLogin';
import { getProductConfig } from '../backend';
import { getProductMode, PRODUCT_MODE } from '../backend/productMode';
import { setCloudApiBase } from '../backend/veCall';
import { APP_VERSION_LABEL } from '../version';
import { serverVersionLabel, versionTag } from '../util/releaseVersion';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../App.css';
import './loginStyle.css';
import {
  saveServingServer,
  clearServingServer,
  saveUserSelection,
  getUserSelection,
  clearUserSelection,
  isPageRefresh
} from '../util/serverPersistence';
import { ServerStatus, checkIsAlive, probeServerAccessible, orderCandidateIps, getClientVantageHost, ACCESSIBILITY_TIMEOUT_MS, formatTimestampAge } from '../util/serverStatus';
import { devProxyUrl } from '../util/getServerInfo';
import { debugLog, debugWarn } from '../util/debug';
import { getServerHistory, addServerToHistory } from '../util/serverHistory';

class Login extends React.Component {
    constructor(props) {
        super(props);

        // Detect if this is a page refresh vs fresh load
        const isRefresh = isPageRefresh();
        debugLog('[LoginView] Page load type:', isRefresh ? 'REFRESH' : 'FRESH LOAD');

        // Always clear stale state on fresh load — on logout we reset to serving server
        if (!isRefresh) {
            clearServingServer();
            clearUserSelection();
            debugLog('[LoginView] Fresh load - cleared serving server and user selection');
        }

        // Pre-populate current server from Redux props (available if page refresh with persisted state)
        const currentIP = window.location.hostname + (window.location.port ? `:${window.location.port}` : '');

        let initialServer = null;
        if (props.bSerial) {
            initialServer = {
                bSerial: props.bSerial,
                sName: props.sName || 'Dividia Video System',
                sVersion: props.sVersion,
                sIP: window.location.hostname,
                sLocalIP: window.location.hostname,
                bPort: parseInt(window.location.port, 10) || 80,
                isCurrent: true,
                isCompatible: true,
                fEview: props.fEview || false
            };
        }

        // Check if user manually selected a server in this session (survives refresh)
        const userSelectedSerial = getUserSelection();

        this.state = {
            showPassword: false,
            connectedServers: initialServer ? [initialServer] : [],
            selectedServer: initialServer,
            serversLoading: true,
            currentServer: props.bSerial ? initialServer : null,
            servingServer: null,
            serverStatuses: {},
            // IP Address field state
            serverAddress: currentIP,
            resolvedIP: currentIP,
            onlineStatus: initialServer ? 'online' : 'checking',
            checkingOnline: false,
            displayVersion: serverVersionLabel({ sVersion: props.sVersion, sReleaseVersion: props.sReleaseVersion, fReleaseCandidate: props.fReleaseCandidate }) || null,
            clientVersion: props.sVersion || null,
            // Page load tracking
            isRefresh: isRefresh,
            userSelectedSerial: userSelectedSerial,
            // Server status modal state
            showStatusModal: false,
            pendingServer: null,
            // Alert modal state
            alertModal: { show: false, message: '', type: 'info' },
            // Startup loading state
            startupFailed: false,
            // Manual/Monarch server — set when user selects a server via the bottom
            // input (Monarch autocomplete or history). Kept separate from connectedServers
            // (top dropdown) to match Java dview's separation of concerns.
            // Login uses: manualServer || selectedServer
            manualServer: null,
            // Monarch autocomplete / server history
            autocompleteResults: [],
            showSuggestions: false,
            autocompleteLoading: false,
            suggestionSource: null,  // 'history' | 'monarch'
            currentTheme: getTheme(),
            // OAuth "Sign in with Microsoft" (ADR-040/047). Read pre-login from
            // config.meta.getStartup: oauthUsers gates the button (>=1 user has an
            // admin-assigned email), oauthApiToken is the serving NVR's serial-JWT
            // used to authenticate the cloudapi /ve/ms/* calls. oauthError is the
            // inline flow-failure message shown by the button.
            oauthUsers: false,
            oauthApiToken: '',
            oauthError: ''
        };

        this._isMounted = true;
        this.connectivityTimer = null;
        this.checkAbortController = null;
        this.statusCheckTimer = null;
        this.backgroundRetryTimer = null;
        this.autocompleteTimer = null;
        this.autocompleteRequestId = 0;
    };

    componentDidMount = async() => {
      // Local NVR product only: fetch the connected-server list (config.meta
      // getStartup) + poll their online status. The cloud product has one backend
      // (cloudapi) and no server list, so this must not run — and with no list to
      // load, the login form renders immediately (clear the loading state).
      if (getProductConfig().hasServerList) {
        this.fetchConnectedServers();
      } else {
        this.setState({ serversLoading: false });
      }

      const username = await localStorage.getItem('username');
      if(username){ this.props.usernameChanged(username) };

      // SECURITY (David 2026-08-14): never restore a password into the form, and PURGE any password a
      // prior build persisted, so shared/field devices get cleaned up on the next login-screen load.
      // Username-only pre-fill is the convenience we keep.
      localStorage.removeItem('password');

      // Listen for theme changes so logo variant updates immediately
      this._onThemeChange = (e) => this.setState({ currentTheme: e.detail });
      window.addEventListener('themechange', this._onThemeChange);

      // KEYBOARD-SAFE HEIGHT (2026-09-01, boss-reported iOS bug): an on-screen keyboard on iOS OVERLAYS
      // the page without shrinking 100vh, so the centered login card (whose content fits in 100vh) can't
      // scroll and the lower buttons — "Sign in with Microsoft" under the "or" — get stranded under the
      // keyboard. Bind the login viewport to window.visualViewport.height (which DOES shrink when the iOS
      // keyboard opens) via the --login-vh CSS var; loginStyle.css caps .login-page to it so the card
      // scrolls into reach. Fallback to innerHeight/100vh where visualViewport is unavailable.
      this._syncLoginVh = () => {
        const vv = window.visualViewport;
        const h = (vv && vv.height) ? vv.height : window.innerHeight;
        document.documentElement.style.setProperty('--login-vh', Math.round(h) + 'px');
      };
      this._syncLoginVh();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._syncLoginVh);
        window.visualViewport.addEventListener('scroll', this._syncLoginVh);
      }
      window.addEventListener('resize', this._syncLoginVh);
    };

    componentWillUnmount() {
      this._isMounted = false;
      // Clean up timers and abort controllers
      if (this.connectivityTimer) {
        clearTimeout(this.connectivityTimer);
      }
      if (this.checkAbortController) {
        this.checkAbortController.abort();
      }
      if (this.statusProbeAbort) {
        this.statusProbeAbort.abort();   // stop the background status sweep on unmount (#86bb7phhh)
      }
      if (this.statusCheckTimer) {
        clearTimeout(this.statusCheckTimer);
      }
      if (this.backgroundRetryTimer) {
        clearInterval(this.backgroundRetryTimer);
      }
      if (this.autocompleteTimer) {
        clearTimeout(this.autocompleteTimer);
      }
      if (this._onThemeChange) {
        window.removeEventListener('themechange', this._onThemeChange);
      }
      // Tear down the keyboard-safe-height listeners + clear the var so other screens use plain 100vh.
      if (this._syncLoginVh) {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._syncLoginVh);
          window.visualViewport.removeEventListener('scroll', this._syncLoginVh);
        }
        window.removeEventListener('resize', this._syncLoginVh);
        document.documentElement.style.removeProperty('--login-vh');
      }
    }

    showAlert = (message, type = 'info') => {
      this.setState({ alertModal: { show: true, message, type } });
    };

    closeAlert = () => {
      this.setState({ alertModal: { show: false, message: '', type: 'info' } });
    };

    /**
     * Check status of all servers in the dropdown list
     * Marks servers as ONLINE, OFFLINE, or FIREWALL
     */
    checkServerStatuses = async (servers) => {
      if (!servers || servers.length === 0) return;

      const newStatuses = {};

      // First pass: HEARTBEAT-based status → dots light up INSTANTLY, zero network (#86bb7phhh #1). A
      // member reporting a recent heartbeat to the controller is shown ONLINE immediately; the
      // background pass below only DOWNGRADES to FIREWALL the ones this client can't reach directly.
      servers.forEach(server => {
        if (server.isCurrent) {
          newStatuses[server.bSerial] = ServerStatus.ONLINE;   // we're connected to it
        } else {
          newStatuses[server.bSerial] = checkIsAlive(server.bTimestamp) ? ServerStatus.ONLINE : ServerStatus.OFFLINE;
        }
      });

      this.setState({ serverStatuses: newStatuses });

      // Second pass: refine each ALIVE member's DIRECT reachability (ONLINE vs FIREWALL) in the
      // BACKGROUND. Was `Promise.all` over ALL servers × BOTH IPs at once (~240 probes, 5s each) which,
      // funneled through the single dev proxy host, saturated the ~6-connection pool and starved the
      // login POST → the "Server Error" / hung login (#86bb7phhh). Now: (a) bounded concurrency below
      // the per-host limit; (b) vantage-ordered probing (probeServerAccessible tries the likely-reachable
      // IP first, skipping the wrong-side 2.5s wait); (c) abortable — the sweep is cancelled the instant a
      // login is submitted (handleLogin) or the view unmounts, so these cosmetic dots can never block login.
      const serversToCheck = servers.filter(s =>
        !s.isCurrent && newStatuses[s.bSerial] !== ServerStatus.OFFLINE
      );

      if (this.statusProbeAbort) this.statusProbeAbort.abort();   // cancel a prior in-flight sweep first, so a second checkServerStatuses run can't commit stale dot values (branch-review nit)
      this.statusProbeAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      const signal = this.statusProbeAbort ? this.statusProbeAbort.signal : undefined;
      const aborted = () => !this._isMounted || !!(signal && signal.aborted);

      const STATUS_PROBE_CONCURRENCY = 4;   // < the ~6/host browser limit → the login POST keeps headroom
      const probeQueue = serversToCheck.slice();
      const runProbeWorker = async () => {
        while (probeQueue.length && !aborted()) {
          const server = probeQueue.shift();
          try {
            const isAccessible = await probeServerAccessible(server, { signal });
            newStatuses[server.bSerial] = isAccessible ? ServerStatus.ONLINE : ServerStatus.FIREWALL;
          } catch (e) {
            newStatuses[server.bSerial] = ServerStatus.FIREWALL;
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(STATUS_PROBE_CONCURRENCY, probeQueue.length) }, () => runProbeWorker())
      );

      if (this._isMounted && !(signal && signal.aborted)) this.setState({ serverStatuses: newStatuses });
    };

    fetchConnectedServers = async (retryCount = 0) => {
      const MAX_RETRIES = 3;
      try {
        debugLog('[LoginView] Fetching connected servers...' + (retryCount > 0 ? ` (retry ${retryCount})` : ''));
        const response = await axios.post('/JSON/', {
          jsonrpc: 2.0,
          method: 'config.meta.getStartup',
          id: 100
        }, { timeout: 5000 });

        const data = response.data?.result?.[1];
        if (data) {
          const general = data[Object.keys(data)[0]];
          const authServers = data[Object.keys(data)[1]] || [];
          const usersOnline = data[Object.keys(data)[2]] || [];
          setCloudApiBase(general?.sCloudApiUrl || null);

          // Client version for compatibility checking
          const clientVersion = general.sVersion || this.props.sVersion;

          // Current server is the one we're connected to (the "serving server")
          // eView capability: backend may set general.fEview directly OR list 'eview'
          // in rgsFeature (older backends). Mirror ServerActions.getServerSettings
          // detection so the login theme matches what redux state will see post-login.
          const currentServerEview = !!(general.fEview || general.rgsFeature?.includes('eview'));
          const currentServer = {
            bSerial: general.bSerial,
            sName: general.sName,
            sVersion: general.sVersion,
            sReleaseVersion: general.sReleaseVersion || '',
            fReleaseCandidate: !!general.fReleaseCandidate,
            sIP: window.location.hostname,
            sLocalIP: window.location.hostname,
            bPort: parseInt(window.location.port, 10) || 80,
            isCurrent: true,
            isCompatible: true,  // Current server is always compatible
            fEview: currentServerEview
          };

          debugLog('[LoginView] Current server:', currentServer.sName, `(${currentServer.bSerial}) v${currentServer.sVersion}`);
          debugLog('[LoginView] Connected servers:', authServers.length);

          // Save the serving server (the NVR hosting this page) - always update with fresh data
          saveServingServer(currentServer);
          const servingServer = currentServer; // Use the fresh data we just got

          // Build server list with version compatibility checking
          const serverList = [currentServer];

          // Add other connected servers (if any)
          authServers.forEach(server => {
            if (server.bSerial !== general.bSerial) {
              const isCompatible = this.checkVersionCompatibility(clientVersion, server.sVersion);
              // Child servers in the authServers payload may also encode eview via
              // rgsFeature (when present) — fall through both like the current server.
              const serverEview = !!(server.fEview || server.rgsFeature?.includes('eview'));
              serverList.push({
                ...server,
                isCurrent: false,
                isCompatible,
                fEview: serverEview
              });
              if (!isCompatible) {
                debugLog(`[LoginView] Server ${server.sName} v${server.sVersion} incompatible with client v${clientVersion}`);
              }
            }
          });

          // Determine which server to select.
          // Simple rule: always default to serving server.
          // On REFRESH only: restore user's dropdown selection if still in connected list.
          const { isRefresh, userSelectedSerial } = this.state;
          let selectedServer = currentServer;
          let serverAddress = window.location.hostname + (currentServer.bPort !== 80 ? `:${currentServer.bPort}` : '');
          let resolvedIP = serverAddress;
          let displayVersion = serverVersionLabel(currentServer);

          if (isRefresh && userSelectedSerial) {
            const userServer = serverList.find(s => s.bSerial === userSelectedSerial);
            if (userServer) {
              debugLog('[LoginView] Restoring user selection on refresh:', userServer.sName);
              selectedServer = userServer;
              displayVersion = serverVersionLabel(userServer);

              if (userServer.isCurrent) {
                serverAddress = window.location.hostname + (userServer.bPort !== 80 ? `:${userServer.bPort}` : '');
              } else {
                const portSuffix = userServer.bPort && userServer.bPort !== 80 ? `:${userServer.bPort}` : '';
                serverAddress = (userServer.sIP || userServer.sLocalIP) + portSuffix;
              }
              resolvedIP = serverAddress;

              if (!userServer.isCurrent) {
                setTimeout(() => this.testServerConnectivity(userServer), 100);
              }
            } else {
              debugLog('[LoginView] Stale user selection (not in list), clearing');
              clearUserSelection();
            }
          } else {
            debugLog('[LoginView] Using serving server:', currentServer.sName);
          }

          // Clear background retry if it was running
          if (this.backgroundRetryTimer) {
            clearInterval(this.backgroundRetryTimer);
            this.backgroundRetryTimer = null;
          }

          this.setState({
            connectedServers: serverList,
            selectedServer: selectedServer,
            currentServer: currentServer,
            servingServer: servingServer,
            serversLoading: false,
            startupFailed: false,
            clientVersion,
            displayVersion: displayVersion,
            serverAddress: serverAddress,
            resolvedIP: resolvedIP,
            onlineStatus: selectedServer.isCurrent ? 'online' : 'checking',
            usersOnline: usersOnline,
            // OAuth rollout gate + serial-JWT for the serving NVR (ADR-047). Both
            // ride in the pre-login getStartup: fOauthUsers on general; the token is
            // the SERVING NVR's, matched by bSerial (NOT authServers[0] — on a
            // controller that can be a member, minting a vouch for the wrong serial).
            oauthUsers: !!(general && general.fOauthUsers),
            oauthApiToken: selectServingApiToken(authServers, general && general.bSerial),
            oauthError: ''
          }, () => {
            // Check server statuses for dropdown indicators
            this.checkServerStatuses(serverList);
          });
        } else {
          this.setState({ serversLoading: false });
        }
      } catch (error) {
        debugWarn('[LoginView] Failed to fetch connected servers:', error.message);
        if (retryCount < MAX_RETRIES) {
          const delay = (retryCount + 1) * 1000;
          debugLog(`[LoginView] Retrying in ${delay}ms...`);
          setTimeout(() => {
            if (this._isMounted) this.fetchConnectedServers(retryCount + 1);
          }, delay);
        } else {
          debugWarn('[LoginView] All retries exhausted');
          if (this._isMounted) this.setState({ serversLoading: false, onlineStatus: 'offline', startupFailed: true });
          // Background retry every 10 seconds
          if (this._isMounted) this.startBackgroundRetry();
        }
      }
    };

    startBackgroundRetry = () => {
      if (!this._isMounted) return;
      if (this.backgroundRetryTimer) clearInterval(this.backgroundRetryTimer);
      this.backgroundRetryTimer = setInterval(() => {
        if (!this._isMounted) {
          clearInterval(this.backgroundRetryTimer);
          return;
        }
        debugLog('[LoginView] Background retry (quiet)...');
        // Retry QUIETLY — do NOT set serversLoading:true here. That re-shows the full-screen
        // "Loading server data" spinner over an already-usable login form every 10s while the
        // server is unreachable (#86bb7phhh E). Keep the form visible; a SUCCESSFUL fetch repaints
        // the server list, a failed one leaves the form + offline state untouched.
        this.fetchConnectedServers(0);
      }, 10000);
    };

    /**
     * Check if server version is compatible with client version
     * Rules from Java dview ServerModel.getListNameAlive():
     * - Major version mismatch: incompatible
     * - Client minor > server minor: incompatible (client too new)
     * - Otherwise: compatible
     */
    checkVersionCompatibility = (clientVersion, serverVersion) => {
      if (!clientVersion || !serverVersion) return true;
      if (clientVersion === '__VERSION__') return true;  // Dev mode
      if (clientVersion === serverVersion) return true;

      const clientParts = clientVersion.split('.');
      const serverParts = serverVersion.split('.');

      if (clientParts.length < 2 || serverParts.length < 2) return true;

      const clientMajor = parseInt(clientParts[0], 10);
      const clientMinor = parseInt(clientParts[1], 10);
      const serverMajor = parseInt(serverParts[0], 10);
      const serverMinor = parseInt(serverParts[1], 10);

      // Major version mismatch
      if (clientMajor !== serverMajor) {
        return false;
      }

      // Client minor > server minor (client is newer)
      if (clientMinor > serverMinor) {
        return false;
      }

      return true;
    };

    handleUsernameChange = (e) => {
        this.props.usernameChanged(e.target.value);
    };

    handlePasswordChange = (e) => {
        this.props.passwordChanged(e.target.value);
    };

    toggleShowPassword = () => {
      this.setState({ showPassword: !this.state.showPassword })
    }

    handleServerChange = (e) => {
      const selectedSerial = parseInt(e.target.value, 10);

      // If user re-selects the current manual server from dropdown, nothing to do
      if (this.state.manualServer && this.state.manualServer.bSerial === selectedSerial) {
        return;
      }

      const server = this.state.connectedServers.find(s => s.bSerial === selectedSerial);
      if (server) {
        // Check if server is incompatible (marked with asterisk)
        if (!server.isCompatible) {
          this.showAlert(`Server "${server.sName}" has an incompatible version (${server.sVersion}). Please update your software.`, 'warning');
          // Reset to first compatible server
          const compatibleServer = this.state.connectedServers.find(s => s.isCompatible);
          if (compatibleServer) {
            this.setState({ selectedServer: compatibleServer });
          }
          return;
        }

        // Check server status and show modal if OFFLINE or FIREWALL
        const status = this.state.serverStatuses[server.bSerial];
        if (status === ServerStatus.OFFLINE || status === ServerStatus.FIREWALL) {
          this.setState({
            pendingServer: server,
            showStatusModal: true
          });
          return;
        }

        // Save user's manual selection to sessionStorage (survives refresh)
        saveUserSelection(server.bSerial);
        debugLog('[LoginView] User manually selected server:', server.sName);

        // Clear any manual/Monarch server — dropdown selection takes priority
        // Also clear retry timer from any previous offline server
        if (this.connectivityTimer) {
          clearTimeout(this.connectivityTimer);
          this.connectivityTimer = null;
        }

        this.setState({
          selectedServer: server,
          manualServer: null,
          displayVersion: serverVersionLabel(server),
          onlineStatus: server.isCurrent ? 'online' : 'checking'
        });

        // Test connectivity to the selected server
        this.testServerConnectivity(server);
      }
    };

    // Handle status modal confirmation - proceed with server selection
    handleStatusModalConfirm = () => {
      const { pendingServer } = this.state;

      // Save user's selection (even for offline/firewall servers)
      if (pendingServer) {
        saveUserSelection(pendingServer.bSerial);
        debugLog('[LoginView] User confirmed selection of offline/firewall server:', pendingServer.sName);
      }

      this.setState({
        showStatusModal: false,
        selectedServer: pendingServer,
        displayVersion: serverVersionLabel(pendingServer),
        onlineStatus: 'checking',
        pendingServer: null
      });

      if (pendingServer) {
        this.testServerConnectivity(pendingServer);
      }
    };

    // Handle status modal cancel
    handleStatusModalCancel = () => {
      this.setState({
        showStatusModal: false,
        pendingServer: null
      });
    };

    /**
     * Test connectivity to a server using whichIP logic
     * Tries: public IP (5s) -> local IP (1s) -> local IP + port (1s) -> fallback to public
     * Also fetches fEview flag from the server
     */
    /**
     * Validate that an IP/hostname string is usable (not an error message)
     * Backend sometimes returns error strings like "system error retrieving ip address"
     */
    isValidAddress = (address) => {
      if (!address) return false;
      if (address === '127.0.0.1') return false;
      // Filter out error message strings from backend
      if (address.toLowerCase().includes('error')) return false;
      if (address.toLowerCase().includes('system')) return false;
      // Must look like an IP or hostname (no spaces)
      if (address.includes(' ')) return false;
      return true;
    };

    testServerConnectivity = async (server) => {
      // Abort any existing check
      if (this.checkAbortController) {
        this.checkAbortController.abort();
      }
      this.checkAbortController = new AbortController();

      this.setState({ checkingOnline: true, onlineStatus: 'checking' });

      // If it's the current server, we know it's online and already have fEview
      if (server.isCurrent) {
        const currentIP = window.location.hostname + (server.bPort !== 80 ? `:${server.bPort}` : '');
        this.setState({
          resolvedIP: currentIP,
          serverAddress: currentIP,
          onlineStatus: 'online',
          checkingOnline: false
        });
        return;
      }

      const { sIP, sLocalIP, bPort } = server;
      const portSuffix = bPort && bPort !== 80 ? `:${bPort}` : '';

      // Vantage-ordered candidate IPs — the SAME primitive the status dots use (#86bb7phhh). Replaces
      // the old hardcoded public(5s)→local(1s)→local+port(1s) list, which (a) duplicated the local/public
      // choice and (b) always ate a full 5s on the public attempt even when the client was on the LAN.
      // orderCandidateIps returns [likely-reachable, fallback] by client vantage and drops junk/127.0.0.1;
      // we probe them in order at the shared 2.5s timeout and stop at the first that answers.
      const attempts = orderCandidateIps(server, getClientVantageHost())
        .map(ip => ({ address: `${ip}${portSuffix}`, timeout: ACCESSIBILITY_TIMEOUT_MS }));

      // Try each address
      for (const attempt of attempts) {
        try {
          debugLog(`[LoginView] Testing ${attempt.address}`);
          const result = await this.checkServerOnlineAndGetInfo(attempt.address, attempt.timeout, server.bSerial);

          if (result.online) {
            debugLog(`[LoginView] Server online at ${attempt.address}, fEview:`, result.fEview);

            // Update the selected server with fEview from the actual server
            const updatedServer = { ...server, fEview: result.fEview };

            this.setState({
              selectedServer: updatedServer,
              resolvedIP: attempt.address,
              serverAddress: attempt.address,
              onlineStatus: 'online',
              checkingOnline: false
            });
            return;
          }
        } catch (e) {
          // Continue to next attempt
          debugLog(`[LoginView] ${attempt.address} failed:`, e.message);
        }
      }

      // All attempts failed - fallback to valid IP for display, or show 'unknown'
      let fallbackIP = 'unknown';
      if (this.isValidAddress(sIP)) {
        fallbackIP = `${sIP}${portSuffix}`;
      } else if (this.isValidAddress(sLocalIP)) {
        fallbackIP = sLocalIP;
      }
      debugLog('[LoginView] All connectivity tests failed, using fallback:', fallbackIP);
      this.setState({
        resolvedIP: fallbackIP,
        serverAddress: fallbackIP,
        onlineStatus: 'offline',
        checkingOnline: false
      });

      // Start retry timer (10 seconds)
      this.startRetryTimer(server);
    };

    /**
     * Check if a server address is online and get server info including fEview
     */
    checkServerOnlineAndGetInfo = async (address, timeout, expectedSerial) => {
      // Route through the dev /server-check proxy in dev (direct in prod) — the SAME transport as the
      // status dots, so a member connectivity test actually works in dev instead of CORS-failing.
      // Translate testServerConnectivity's AbortController → axios-0.19 CancelToken so re-triggering the
      // check actually CANCELS the prior in-flight probe (0.19 ignores `signal`; mirrors checkIsAccessible). #86bb7phhh
      const signal = this.checkAbortController && this.checkAbortController.signal;
      const config = { timeout };
      let cancelSource = null;
      let onAbort = null;
      if (signal && typeof axios.CancelToken !== 'undefined' && axios.CancelToken.source) {
        cancelSource = axios.CancelToken.source();
        config.cancelToken = cancelSource.token;
        onAbort = () => cancelSource.cancel('aborted');
        if (signal.addEventListener) signal.addEventListener('abort', onAbort);
      }
      try {
        const response = await axios.post(devProxyUrl(`http://${address}/JSON/`), {
          jsonrpc: 2.0,
          method: 'config.meta.getStartup',
          id: 101
        }, config);

        const data = response.data?.result?.[1];
        if (data) {
          const general = data[Object.keys(data)[0]];
          const serial = general?.bSerial;

          // Verify serial matches expected server
          if (expectedSerial && serial !== expectedSerial) {
            debugLog(`[LoginView] Serial mismatch: expected ${expectedSerial}, got ${serial}`);
            return { online: false };
          }

          // Extract fEview from rgsFeature or direct property
          const fEview = general?.fEview || general?.rgsFeature?.includes('eview') || false;

          return { online: true, fEview };
        }
        return { online: false };
      } catch (e) {
        return { online: false };
      } finally {
        if (onAbort && signal && signal.removeEventListener) signal.removeEventListener('abort', onAbort);
      }
    };

    /**
     * Start a 10-second retry timer for offline servers
     */
    startRetryTimer = (server) => {
      if (this.connectivityTimer) {
        clearTimeout(this.connectivityTimer);
      }
      this.connectivityTimer = setTimeout(() => {
        // Only retry if still on the same server and still offline
        if (this.state.selectedServer?.bSerial === server.bSerial &&
            this.state.onlineStatus === 'offline') {
          debugLog('[LoginView] Retrying connectivity check...');
          this.testServerConnectivity(server);
        }
      }, 10000);
    };

    /**
     * Handle manual IP address change
     */
    // --- IP/hostname validation (mirrors Java InternetAddress.java) ---

    isValidIPv4 = (str) => {
      const bare = str.split(':')[0]; // strip port
      return /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/.test(bare);
    };

    isValidHostname = (str) => {
      const bare = str.split(':')[0]; // strip port
      // Must have at least one dot (rules out bare words triggering direct connect)
      return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(bare);
    };

    // --- Monarch autocomplete API (mirrors Java AuthHelper.autoCompleteServer) ---

    monarchAutocomplete = async (search) => {
      // Always use /monarch/JSON/ — proxied by Apache in production,
      // by setupProxy.js in dev. Avoids CORS from direct cross-origin call.
      const url = '/monarch/JSON/';

      try {
        const response = await axios.post(url, {
          jsonrpc: 2.0,
          method: 'stats.autocompleteServer',
          params: [search],
          id: 200
        }, { timeout: 5000 });

        const result = response.data?.result?.[1];
        if (Array.isArray(result)) {
          return result;
        }
        // Single object result from lookupServerByCompanyCode shape
        if (result && typeof result === 'object' && !Array.isArray(result)) {
          return [result];
        }
        return [];
      } catch (e) {
        debugLog('[LoginView] Monarch autocomplete failed:', e.message);
        return [];
      }
    };

    // --- Server alive check (mirrors Java Server.checkIsAlive — 10hr threshold) ---

    checkServerIsAlive = (bTimestamp) => {
      if (!bTimestamp) return false;
      const now = Math.floor(Date.now() / 1000);
      return (now - bTimestamp) < 36000;
    };

    // --- Build best connection address from Server object ---
    // Mirrors Java LoginController.java:975-987 (findServer → extract address)

    buildServerAddress = (server) => {
      if (server.sHostname) return server.sHostname;
      const portSuffix = server.bPort && server.bPort !== 80 ? `:${server.bPort}` : '';
      if (server.sIP && server.sIP !== '127.0.0.1') return server.sIP + portSuffix;
      if (server.sLocalIP && server.sLocalIP !== '127.0.0.1') return server.sLocalIP + portSuffix;
      return server.sIP + portSuffix;
    };

    // --- Modified handleAddressChange: three-branch logic (mirrors Java keyReleased) ---

    handleAddressChange = (e) => {
      const value = e.target.value;
      this.setState({ serverAddress: value });

      const trimmed = value.trim();

      // Clear any pending autocomplete
      if (this.autocompleteTimer) {
        clearTimeout(this.autocompleteTimer);
        this.autocompleteTimer = null;
      }

      // Branch 1: Empty or < 3 chars → show server history
      if (trimmed.length < 3) {
        const history = getServerHistory();
        if (history.length > 0) {
          this.setState({
            autocompleteResults: history.map(h => ({ ...h, isHistory: true })),
            showSuggestions: true,
            suggestionSource: 'history',
            autocompleteLoading: false
          });
        } else {
          this.setState({ showSuggestions: false, autocompleteResults: [] });
        }
        return;
      }

      // Branch 2: Valid IP or hostname → skip Monarch, hide suggestions
      if (this.isValidIPv4(trimmed) || this.isValidHostname(trimmed)) {
        this.setState({ showSuggestions: false, autocompleteResults: [] });
        return;
      }

      // Branch 3: 3+ chars, not IP/hostname → debounced Monarch autocomplete
      const requestId = ++this.autocompleteRequestId;
      this.setState({ autocompleteLoading: true });

      this.autocompleteTimer = setTimeout(async () => {
        const results = await this.monarchAutocomplete(trimmed);
        // Guard against stale results
        if (requestId !== this.autocompleteRequestId) return;

        this.setState({
          autocompleteResults: results,
          showSuggestions: results.length > 0,
          suggestionSource: 'monarch',
          autocompleteLoading: results.length === 0 ? false : false
        });
      }, 300);
    };

    // --- Focus: show history when input is empty/short ---

    handleAddressFocus = () => {
      // Save current value so we can restore on blur if user didn't type anything
      this.savedAddress = this.state.serverAddress || '';
      // Clear input so user can start typing immediately
      this.setState({ serverAddress: '' }, () => {
        // Show history when input is cleared
        const history = getServerHistory();
        if (history.length > 0) {
          this.setState({
            autocompleteResults: history.map(h => ({ ...h, isHistory: true })),
            showSuggestions: true,
            suggestionSource: 'history',
            autocompleteLoading: false
          });
        }
      });
    };

    // --- Blur: delayed hide to allow click to register ---

    handleAddressBlur = () => {
      setTimeout(() => {
        // If user didn't type anything, restore the previous value
        if (!this.state.serverAddress && this.savedAddress) {
          this.setState({ serverAddress: this.savedAddress, showSuggestions: false });
        } else {
          this.setState({ showSuggestions: false });
        }
      }, 200);
    };

    // --- Suggestion selected (from autocomplete or history) ---

    handleSuggestionSelect = (server) => {
      const address = this.buildServerAddress(server);
      debugLog('[LoginView] Suggestion selected:', server.sName, '→', address);

      this.setState({
        serverAddress: address,
        showSuggestions: false,
        autocompleteResults: []
      }, () => {
        // Auto-trigger Set button connectivity check
        this.handleSetAddress(server);
      });
    };

    /**
     * Handle Set button click - test connectivity to manually entered address.
     *
     * Separation of concerns (matches Java dview):
     * - If the server matches one in the connected list (top dropdown): switch dropdown to it
     * - If NOT in connected list: set as manualServer (does NOT pollute the dropdown)
     *
     * If a Monarch server object is passed, use its metadata to build a richer server entry.
     */
    handleSetAddress = async (monarchServer = null) => {
      const { serverAddress, connectedServers } = this.state;
      if (!serverAddress) return;

      // Clear retry timer from any previous check
      if (this.connectivityTimer) {
        clearTimeout(this.connectivityTimer);
        this.connectivityTimer = null;
      }

      this.setState({ checkingOnline: true, onlineStatus: 'checking' });

      try {
        // Test connectivity and get serial from the server.
        // devProxyUrl → dev: /server-check/ proxy (avoids CORS); prod: direct request. Shared with the
        // status dots + connectivity test so there's one dev-transport helper, not three copies. #86bb7phhh
        const isDev = process.env.NODE_ENV === 'development';
        const checkUrl = devProxyUrl(`http://${serverAddress}/JSON/`);

        let response = null;
        try {
          response = await axios.post(checkUrl, {
            jsonrpc: 2.0,
            method: 'config.meta.getStartup',
            id: 102
          }, { timeout: 6000 });
        } catch (httpErr) {
          if (!isDev) {
            // In production, HTTP failed — try HTTPS
            debugLog(`[LoginView] HTTP failed for ${serverAddress}, trying HTTPS...`);
            try {
              response = await axios.post(`https://${serverAddress}/JSON/`, {
                jsonrpc: 2.0,
                method: 'config.meta.getStartup',
                id: 102
              }, { timeout: 5000 });
            } catch (httpsErr) {
              throw httpErr;
            }
          } else {
            throw httpErr;
          }
        }

        const data = response.data?.result?.[1];
        if (data) {
          const general = data[Object.keys(data)[0]];
          const serverSerial = general?.bSerial;
          const serverName = general?.sName;
          const serverVersion = general?.sVersion;
          const serverFEview = general?.fEview || general?.rgsFeature?.includes('eview') || false;

          debugLog(`[LoginView] Manual IP ${serverAddress} resolved to: ${serverName} (${serverSerial}) v${serverVersion}, fEview:`, serverFEview);

          // Save to history (no bTimestamp — it goes stale and causes false offline)
          addServerToHistory({
            bSerial: serverSerial,
            sName: serverName,
            sIP: general?.sIP || monarchServer?.sIP || serverAddress.split(':')[0],
            sLocalIP: general?.sLocalIP || monarchServer?.sLocalIP || '',
            bPort: general?.bPort || monarchServer?.bPort || parseInt(serverAddress.split(':')[1], 10) || 80,
            sHostname: monarchServer?.sHostname || ''
          });

          // Check if this server is in our connected servers list
          const matchingServer = connectedServers.find(s => s.bSerial === serverSerial);

          if (matchingServer) {
            // Found in connected list — switch dropdown to this server, clear manualServer
            debugLog(`[LoginView] Found in connected list, switching dropdown to ${matchingServer.sName}`);
            const updatedServer = { ...matchingServer, fEview: serverFEview };

            saveUserSelection(matchingServer.bSerial);

            this.setState({
              selectedServer: updatedServer,
              manualServer: null,  // Clear — using connected server
              resolvedIP: serverAddress,
              serverAddress: serverAddress,
              displayVersion: serverVersionLabel(matchingServer),
              onlineStatus: 'online',
              checkingOnline: false
            });
          } else {
            // NOT in connected list — set as manualServer (don't add to dropdown)
            // This matches Java dview: cbxServer (dropdown) stays clean,
            // entServer (input) shows the manual/Monarch server
            debugLog(`[LoginView] Server ${serverName} (${serverSerial}) not in connected list — setting as manual server`);

            const manual = {
              bSerial: serverSerial,
              sName: serverName,
              sVersion: serverVersion,
              sReleaseVersion: general?.sReleaseVersion || '',
              fReleaseCandidate: !!general?.fReleaseCandidate,
              sIP: general?.sIP || monarchServer?.sIP || serverAddress.split(':')[0],
              sLocalIP: general?.sLocalIP || monarchServer?.sLocalIP || '',
              bPort: general?.bPort || monarchServer?.bPort || parseInt(serverAddress.split(':')[1], 10) || 80,
              sHostname: monarchServer?.sHostname || '',
              isCurrent: false,
              isCompatible: true,
              fEview: serverFEview
            };

            this.setState({
              manualServer: manual,
              resolvedIP: serverAddress,
              serverAddress: serverAddress,
              displayVersion: serverVersionLabel(manual),
              onlineStatus: 'online',
              checkingOnline: false
            });
          }
        } else {
          this.setState({
            resolvedIP: serverAddress,
            onlineStatus: 'offline',
            checkingOnline: false
          });
        }
      } catch (e) {
        debugLog(`[LoginView] Failed to connect to ${serverAddress}:`, e.message);
        this.setState({
          resolvedIP: serverAddress,
          onlineStatus: 'offline',
          checkingOnline: false
        });
      }
    };

    keyPressed = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        // Prevent multiple submissions while loading
        if (this.props.loading) return;
        // eView mode is determined by the selected server's fEview flag
        this.handleLogin(false, this.getSelectedServerEview());
      }
    }

    onSubmit = async ( e ) => {
        e.preventDefault();
        // Prevent multiple submissions while loading
        if (this.props.loading) return;
        // eView mode is determined by the selected server's fEview flag
        await this.handleLogin(false, this.getSelectedServerEview());
    };

    handleLogin = async (fForce, fEviewMode = false) => {
      // Login preempts the cosmetic server-status probes — abort the whole background sweep so the auth
      // chain gets the full connection pool instead of starving behind ~240 in-flight probes. #86bb7phhh
      if (this.statusProbeAbort) this.statusProbeAbort.abort();
      const { username, password } = this.props;

      // Products without a local NVR session (cloud) have ONE backend — no server
      // target, force, or eView. Route to the adapter-backed cloud login. This is
      // the login screen's single product-awareness point (a named capability, not
      // a mode literal); the local flow below is unchanged.
      if (!getProductConfig().hasLocalNvrSession) {
        await this.props.loginCloud(username, password);
        return;
      }

      const target = this.getLoginTarget();

      // eView mode always forces (enterprise viewer manages its own sessions)
      const useForce = fEviewMode ? true : fForce;

      if (fEviewMode) {
        debugLog('[LoginView] Logging in with eView (enterprise mode) - using fForce=true');
      }

      // If target is the current server (or null), use normal login
      if (!target || target.isCurrent) {
        await this.props.loginUser(username, password, useForce, false, this.props.bSerial, fEviewMode);
      } else {
        // Login to a different server - use loginToServer action
        debugLog('[LoginView] Logging into different server:', target.sName);
        await this.props.loginToServer(target, username, password, useForce, fEviewMode);
      }
    };

    /**
     * Whether to offer "Sign in with Microsoft". OAuth binds to a user on THIS
     * (serving) NVR, and the rollout gate (fOauthUsers) + serial-JWT only come in
     * the serving NVR's getStartup — so the button shows only when the gate is set
     * AND the selected login target is the current server. A jumped/other server
     * has no known OAuth state here, so it hides (fail-safe).
     */
    canShowOauth = () => {
      // Hosted CloudNVR viewer: ALWAYS offer "Sign in with Microsoft" (David's ask —
      // every cloud username is already an email, card 86bbk70nq). The portal flow
      // (loginCloudMicrosoft) needs no serving-NVR serial-JWT.
      const mode = getProductMode();
      if (mode === PRODUCT_MODE.CLOUD) return true;
      // EEN viewer has no Microsoft OAuth.
      if (mode === PRODUCT_MODE.EEN) return false;
      // LOCAL appliance: the existing serving-NVR gate.
      if (!getProductConfig().hasLocalNvrSession) return false;
      if (!this.state.oauthUsers || !this.state.oauthApiToken) return false;
      const target = this.getLoginTarget();
      return !target || target.isCurrent;
    };

    /**
     * Start the "Sign in with Microsoft" flow. Opens a blank popup SYNCHRONOUSLY
     * inside this click gesture (so the browser doesn't block it — the authUrl
     * isn't known until after the /authurl round-trip), then hands loginMicrosoft
     * a navigate() that points that popup at the authUrl. On an OAuth-flow failure
     * the message is shown inline by the button; terminal NVR states (noauth/etc.)
     * flow through the existing LOGIN_RESULT modals. Mirrors dview's
     * LoginController.startMicrosoftLogin.
     */
    startMicrosoftLogin = async () => {
      if (this.props.loading) return;
      this.setState({ oauthError: '' });

      const fEviewMode = this.getSelectedServerEview();
      const bSerial = (this.state.currentServer && this.state.currentServer.bSerial) || this.props.bSerial;

      // Open the popup up front (user gesture) so it isn't blocked; fill it later.
      let popup = null;
      try {
        popup = window.open('', 'dividia_msft_login', 'width=520,height=680');
      } catch (e) { popup = null; }
      if (!popup) {
        this.setState({ oauthError: 'Could not open the Microsoft sign-in window. Please allow pop-ups and try again.' });
        return;
      }

      const navigate = (url) => { try { popup.location.href = url; } catch (e) { /* cross-origin set is allowed */ } };
      // If the user closes the popup, stop polling promptly instead of freezing the
      // login form for the whole budget. (Reading .closed on a cross-origin window
      // is allowed; guard anyway.)
      const isCancelled = () => { try { return !!(popup && popup.closed); } catch (e) { return false; } };

      try {
        // Hosted CloudNVR: the browser-started portal flow (no serving NVR / serial).
        // Appliance: the serial-JWT vouch flow. Same popup + navigate + isCancelled.
        const res = getProductMode() === PRODUCT_MODE.CLOUD
          ? await this.props.loginCloudMicrosoft({ navigate, isCancelled })
          : await this.props.loginMicrosoft({
              bSerial,
              sApiToken: this.state.oauthApiToken,
              fEviewMode,
              navigate,
              isCancelled
            });
        // Only a genuine flow failure shows inline; a user cancel (code 'cancelled')
        // and terminal states routed through LOGIN_RESULT are handled elsewhere.
        if (res && !res.ok && res.code === 'flow') {
          this.setState({ oauthError: res.message || 'Microsoft sign-in failed.' });
        }
      } finally {
        // The cloudapi callback page self-closes a script-opened tab; close ours too
        // in case it didn't (failure/timeout paths leave it open on login.microsoft…).
        try { if (popup && !popup.closed) popup.close(); } catch (e) { /* ignore */ }
      }
    };

    /**
     * Get the active login target server.
     * manualServer (from bottom input / Monarch) takes priority over
     * selectedServer (from top dropdown) — matches Java dview's separation.
     */
    getLoginTarget = () => {
      return this.state.manualServer || this.state.selectedServer;
    };

    /**
     * Check if selected server has eView enabled
     * Returns false while loading to prevent flash of wrong theme
     */
    getSelectedServerEview = () => {
      const target = this.getLoginTarget();

      // Check target server's fEview - this is the authoritative source.
      // With Redux Persist, the initial selectedServer has a reliable fEview
      // value from either persisted props or the serving server, so we can trust it
      // even while serversLoading is true (no more flash of wrong theme).
      if (target && target.fEview !== undefined) {
        return target.fEview;
      }

      // Fallback to persisted Redux props
      return this.props.fEview || false;
    };

    /**
     * Get the base URL for download links based on selected server
     * Uses the resolved IP from connectivity testing
     */
    getServerBaseUrl = () => {
      const target = this.getLoginTarget();
      const { resolvedIP } = this.state;

      // If no selected server or it's the current server, use relative URLs
      if (!target || target.isCurrent) {
        return '';
      }

      // Use the resolved IP from connectivity testing
      if (resolvedIP) {
        return `http://${resolvedIP}`;
      }

      // Fallback - build URL from server info
      const { sIP, sLocalIP, bPort } = target;
      const portSuffix = bPort && bPort !== 80 ? `:${bPort}` : '';

      if (sIP && sIP !== '127.0.0.1') {
        return `http://${sIP}${portSuffix}`;
      }

      if (sLocalIP && sLocalIP !== '127.0.0.1') {
        return `http://${sLocalIP}${portSuffix}`;
      }

      return '';
    };
    
  render() {
    const date = new Date();
    const currentYear = date.getFullYear();
    const isEview = this.getSelectedServerEview();

    // The mobile-landscape login block (loginStyle.css) is built for the LOCAL / eView login, which is
    // CROWDED — server dropdown + server-IP box + Microsoft OAuth + native-app downloads. To fit that on
    // a ~390px-tall screen it drops the logo + card title and top-aligns so the content can scroll. The
    // CLOUD login has NONE of that chrome, so it is SPARSE and that compaction only strips its branding
    // and centering. Gate the restore on hasServerBox — the capability that literally means "shows the
    // server-selection input" (David 2026-08-20), i.e. the actual reason the layout differs — NOT
    // hasLocalNvrSession, and NOT a getProductMode() literal (productConfig's contract: read a named
    // capability, never test the mode). eView is product mode 'local' → hasServerBox true → it keeps the
    // old landscape exactly; only cloud (hasServerBox false) diverges. (#86bbfuajj)
    const hasServerChrome = getProductConfig().hasServerBox;

    return (
      <div className={`login-page login-container${hasServerChrome ? '' : ' login-page--no-server-chrome'}`}>
        {/* Alert Modal for version incompatibility */}
        <AlertModal
          show={this.state.alertModal.show}
          message={this.state.alertModal.message}
          type={this.state.alertModal.type}
          eviewMode={isEview}
          onClose={this.closeAlert}
        />

        {/* Server Status Confirmation Modal */}
        <ServerStatusModal
          show={this.state.showStatusModal}
          serverName={this.state.pendingServer?.sName || ''}
          serverStatus={this.state.pendingServer ? this.state.serverStatuses[this.state.pendingServer.bSerial] : null}
          lastSeen={this.state.pendingServer ? formatTimestampAge(this.state.pendingServer.bTimestamp) : ''}
          isConnecting={false}
          connectionError={null}
          onCancel={this.handleStatusModalCancel}
          onConfirm={this.handleStatusModalConfirm}
        />

        <div className="login-inner">
          {/* PRIMARY column = logo + form/modals. Grouped so LANDSCAPE can lay the login out in two
              columns (#86bb7phhh). These wrappers are display:contents everywhere EXCEPT mobile
              landscape, so portrait / tablet / desktop layout is byte-for-byte unchanged. */}
          <div className="login-primary">
            <img src={this.state.currentTheme === 'dark' ? dividia_logo_white : dividia_logo_dark} className="login-logo" alt="Dividia Logo" />

            {/* Loading state — waiting for getStartup (local NVR product only) */}
            { getProductConfig().hasServerList && this.state.serversLoading && this.state.connectedServers.length === 0 && !this.state.startupFailed && (
              <div className="login-form login-loading-state">
                <h3 className="login-form__title">{getProductConfig().viewerName || 'Dividia Lite Viewer'}</h3>
                <div className="login-loading-state__content">
                  <Loader type="TailSpin" color="white" height={32} width={32} />
                  <p className="login-loading-state__text">Loading server data...</p>
                </div>
              </div>
            )}

            {/* Error state — getStartup failed */}
            { this.state.startupFailed && this.state.connectedServers.length === 0 && (
              <div className="login-form login-loading-state">
                <h3 className="login-form__title">{getProductConfig().viewerName || 'Dividia Lite Viewer'}</h3>
                <div className="login-loading-state__content">
                  <p className="login-loading-state__text login-loading-state__text--error">Unable to reach server</p>
                  <p className="login-loading-state__subtext">Retrying automatically...</p>
                  <button
                    className="login-button login-button--compact hoverable"
                    onClick={() => { this.setState({ serversLoading: true, startupFailed: false }); this.fetchConnectedServers(0); }}
                    style={{marginTop: '12px'}}
                  >
                    Retry Now
                  </button>
                </div>
              </div>
            )}

            {/* Full login form — only shown when we have server data */}
            { this.props.loginResult !== 'maxsession' &&
              this.props.loginResult !== 'error' &&
              this.props.loginResult !== 'expired' &&
              this.props.loginResult !== 'exists' &&
              this.props.loginResult !== 'pwexp' &&
              this.props.loginResult !== 'changepw' &&
              this.props.loginResult !== 'inactive' &&
              (this.state.connectedServers.length >= 1 || !getProductConfig().hasServerList) ?

              <form className={`login-form login-form-container ${isEview ? 'eview-mode' : ''}`}>
                  <h3 className="login-form__title">{getProductConfig().viewerName || (isEview ? 'Enterprise Lite Viewer' : 'Dividia Lite Viewer')}</h3>

                  {/* Server Selection — local NVR product only (cloud has one backend) */}
                  {getProductConfig().hasServerList && (
                  <div className="login-server-select-container">
                      <select
                        className="login-server-select"
                        value={this.getLoginTarget()?.bSerial || ''}
                        onChange={this.handleServerChange}
                      >
                        {this.state.connectedServers.map(server => {
                          const status = this.state.serverStatuses[server.bSerial];
                          let statusSuffix = '';
                          if (status === ServerStatus.OFFLINE) {
                            statusSuffix = ' [OFFLINE]';
                          } else if (status === ServerStatus.FIREWALL) {
                            statusSuffix = ' [FIREWALL]';
                          }
                          return (
                            <option key={server.bSerial} value={server.bSerial}>
                              {server.sName}{!server.isCompatible ? '*' : ''}{statusSuffix}
                            </option>
                          );
                        })}
                        {/* Temporary entry for Monarch/manual server — not persisted to connectedServers */}
                        {this.state.manualServer && !this.state.connectedServers.find(s => s.bSerial === this.state.manualServer.bSerial) && (
                          <option key={this.state.manualServer.bSerial} value={this.state.manualServer.bSerial}>
                            {this.state.manualServer.sName}
                          </option>
                        )}
                      </select>
                    </div>
                  )}

                  {/* username + password wrapped so LANDSCAPE can place them side-by-side (label
                      above input), saving a row of height. display:contents in portrait/tablet/
                      desktop so those keep stacking unchanged. (#86bb7phhh) */}
                  <div className="login-credentials">
                  <div className="login-field">
                      <label htmlFor="username" className="login-field__label">Username:</label>
                      <input
                          className="login-input"
                          type="text"
                          name="username"
                          autoComplete="username"
                          value={this.props.username}
                          onChange={this.handleUsernameChange}
                          autoFocus={true}
                      />
                  </div>

                  <div className="login-field login-field--password">
                    {/* label OUTSIDE __row so landscape can stack it above the input; __row stays
                        display:contents in portrait, so label+input+eye still read as one row there. */}
                    <label htmlFor="password" className="login-field__label">Password:</label>
                    <div className="login-field__row">
                      <input
                          className="login-input"
                          type={this.state.showPassword ? 'text' : 'password'}
                          name="password"
                          autoComplete="current-password"
                          value={this.props.password}
                          onKeyPress={this.keyPressed}
                          onChange={this.handlePasswordChange}
                      />
                      {this.state.showPassword ? (
                        <FaEyeSlash
                          className="login-field__icon"
                          onClick={() => this.toggleShowPassword()}
                        />
                      ) : (
                        <FaEye
                          className="login-field__icon"
                          onClick={() => this.toggleShowPassword()}
                        />
                      )}
                    </div>
                  </div>
                  </div>{/* end .login-credentials */}

                  <div className="login-toggle-container">
                  </div>

                  <p className={`login-error ${this.props.loginResult === 'pwchanged' ? 'login-error--notice' : ''} ${shouldShowLoginError(this.props.loginResult, this.props.loading) ? '' : 'login-error--hidden'}`}>
                    {this.props.loginResult === 'invalidcreds'
                      ? 'Invalid username or password'
                      : this.props.loginResult === 'pwchanged'
                      ? 'Your password was updated. Please sign in with your new password.'
                      : (this.props.loginResult === 'noauth' ? 'Access Denied' : 'Must provide username and password')}
                  </p>

                  {/* Login is the ONLY action in the form now — the native-viewer / App-Store
                      download links moved out of the card to their own group below the server box
                      (#86bb7phhh). Full-width primary CTA. */}
                  {/* Login + Microsoft wrapped so LANDSCAPE can place them on ONE row with Login in
                      the larger, more-prominent space. display:contents elsewhere, so portrait /
                      tablet / desktop keep Login full-width above the "or" + Microsoft. (#86bb7phhh) */}
                  <div className="login-actions">
                  <div className="login-button-row login-button-row--solo">
                        <button
                          className={`login-button login-button--full hoverable ${this.props.loading ? 'login-button--loading' : ''}`}
                          onClick={this.onSubmit}
                          disabled={this.props.loading}
                        >
                          {/* Text always in DOM for sizing — invisible when loading */}
                          <span style={{ visibility: this.props.loading ? 'hidden' : 'visible' }}>Login</span>
                          {/* Spinner overlaid on top — button never changes size */}
                          {this.props.loading && (
                            <span className="login-button__spinner-wrap">
                              <Loader type="TailSpin" color="#222" height={18} width={18} />{/* dark: the login button is lightgrey, white was invisible (#86bb7phhh) */}
                            </span>
                          )}
                        </button>
                  </div>

                  {/* OAuth "Sign in with Microsoft" (ADR-040/047): a full-width
                      provider button under an "or" separator, so credentials-vs-
                      Microsoft reads as an either/or choice. Shown only when the
                      serving NVR has >=1 user with an admin-assigned email
                      (fOauthUsers) — hidden by default. Mirrors dview's LoginView. */}
                  {/* OrSeparator is a SIBLING of the Login row + oauth block (not inside oauth) so the
                      landscape row can place "or" BETWEEN the two buttons. In portrait it flows
                      normally (Login / or / Microsoft stacked). (#86bb7phhh) */}
                  {this.canShowOauth() && <OrSeparator />}
                  {this.canShowOauth() && (
                    <div className="login-oauth">
                      <OauthProviderButton
                        label="Sign in with Microsoft"
                        onClick={this.startMicrosoftLogin}
                        disabled={this.props.loading}
                      />
                      {this.state.oauthError && (
                        <p className="login-error login-oauth__error" data-testid="login.oauth-error">
                          {this.state.oauthError}
                        </p>
                      )}
                    </div>
                  )}
                  </div>{/* end .login-actions */}

              </form> :
            null
            }

            { this.props.loginResult === 'maxsession' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <MaxSessionsModal
                  eviewMode={this.getSelectedServerEview()}
                  onAccept={() => this.props.clearSessionModal()}
                />
              </div> :
              null
            }

            { this.props.loginResult === 'error' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <SessionErrorModal
                  eviewMode={this.getSelectedServerEview()}
                  onAccept={() => this.props.clearSessionModal()}
                />
              </div> :
              null
            }

            { this.props.loginResult === 'expired' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <SessionExpiredModal
                  eviewMode={this.getSelectedServerEview()}
                  reason={this.props.sessionEndReason}
                  /* Support info is opt-in per-reason: only surfaced when whoever ended the
                     session flagged it anomalous (reason.support === true). A plain noauth
                     (routine timeout / sign-in elsewhere) leaves it off — no false-alarm calls. */
                  showSupportInfo={!!(this.props.sessionEndReason && this.props.sessionEndReason.support)}
                  onAccept={() => this.props.clearSessionModal()}
                />
              </div> :
              null
            }

            { this.props.loginResult === 'exists' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <SessionExistsModal
                  eviewMode={this.getSelectedServerEview()}
                  onDeny={() => this.props.clearSessionModal()}
                  onAccept={() => this.handleLogin(true, this.getSelectedServerEview())}
                />
              </div> :
              null
            }

            { this.props.loginResult === 'pwexp' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <PasswordExpiredModal
                  eviewMode={this.getSelectedServerEview()}
                  onAccept={() => this.props.clearSessionModal()}
                />
              </div> :
              null
            }

            {/* Cloud FIRST-LOGIN / admin-reset forced password change. Entered when
                loginCloud sees /ve/loginV2 return { changePassword:true } (no token yet).
                The modal collects a new password → changePasswordCloud (→ /ve/changepassword
                → re-login → /live). Cancel returns to the login form. */}
            { this.props.loginResult === 'changepw' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <CloudPasswordChangeModal
                  eviewMode={this.getSelectedServerEview()}
                  onSubmit={(newPassword) => this.props.changePasswordCloud(newPassword)}
                  onCancel={() => this.props.clearSessionModal()}
                />
              </div> :
              null
            }

            { this.props.loginResult === 'inactive' ?
              <div className={`login-modal-wrapper ${this.getSelectedServerEview() ? 'eview-mode' : ''}`}>
                <InactivityModal
                  eviewMode={this.getSelectedServerEview()}
                  onAccept={() => this.props.clearSessionModal()}
                />
              </div> :
              null
            }
          </div>{/* end PRIMARY column */}

          {/* SECONDARY column = server box + native-app downloads + footer. */}
          <div className="login-secondary">
            {/* Server Address Box — IP, hostname, or store name search (Monarch). Gated on the
                hasServerBox capability: this whole "which local NVR am I connecting to" widget is
                shown in LOCAL and hidden in CLOUD (cloudapi is the single fixed backend — no NVR to
                pick). Distinct from hasServerList; NOT gated via hasLocalNvrSession (David 2026-08-20). */}
            {getProductConfig().hasServerBox && this.state.connectedServers.length >= 1 &&
              <div className={`login-server-box ${isEview ? 'eview-mode' : ''}`}>
                <div className="login-server-box__row">
                  <input
                    type="text"
                    className="login-server-box__input"
                    value={this.state.serverAddress}
                    onChange={this.handleAddressChange}
                    onFocus={this.handleAddressFocus}
                    onBlur={this.handleAddressBlur}
                    placeholder="IP address, hostname, or store name"
                    autoComplete="off"
                  />
                  <button
                    className="login-server-box__button hoverable"
                    onClick={() => this.handleSetAddress()}
                    disabled={this.state.checkingOnline}
                  >
                    Set
                  </button>
                </div>
                {/* Autocomplete / History suggestion dropdown */}
                {this.state.showSuggestions && this.state.autocompleteResults.length > 0 && (
                  <div className="login-server-box__suggestions">
                    {this.state.autocompleteLoading && (
                      <div className="login-server-box__suggestion--loading">Searching...</div>
                    )}
                    {this.state.autocompleteResults.map((server, idx) => (
                      <div
                        key={server.bSerial || idx}
                        className={`login-server-box__suggestion${server.isHistory ? ' login-server-box__suggestion--history' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); this.handleSuggestionSelect(server); }}
                      >
                        <span className="login-server-box__suggestion-name">{server.sName}</span>
                        {server.bSerial && (
                          <span className="login-server-box__suggestion-serial">[{server.bSerial}]</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="login-server-box__row">
                  <span className="login-server-box__ip">{this.state.resolvedIP || ''}</span>
                  <span className={`login-server-box__status login-server-box__status--${this.state.onlineStatus}`}>
                    {this.state.onlineStatus === 'online' ? 'Online' :
                     this.state.onlineStatus === 'checking' ? 'Connecting...' : 'Offline'}
                  </span>
                  {!isEview && this.state.usersOnline && (
                    <span
                      className="login-server-box__users-online"
                      title={this.state.usersOnline.length > 0 ? `Online: ${this.state.usersOnline.join(', ')}` : ''}
                    >
                      {this.state.usersOnline.length} {this.state.usersOnline.length === 1 ? 'User' : 'Users'}
                    </span>
                  )}
                </div>
              </div>
            }

            {/* BOTTOM group — downloads + footer. Normally `display: contents` (transparent, so
                portrait / tablet / desktop layout is unaffected); at mobile-landscape it flips to a
                flex row: downloads left (smaller), footer text multi-line to their right — reclaims
                the vertical space the stacked version would need. (#86bb7phhh) */}
            <div className="login-bottom">
            {/* Native-app downloads — MOVED OUT of the login card (#86bb7phhh). The form is now
                auth-only; these launch the native desktop viewer / mobile app. They sit between the
                server box and the footer, styled as themed secondary buttons (page-level, not the
                card's white pills). Each keeps its original platform gating + label. */}
            {getProductConfig().hasViewerDownloads && this.props.platform !== 'Android' && (
              <div className="login-native-apps">
                {this.props.platform !== 'Ios' && (
                  isEview ? (
                    <a
                      href={this.getServerBaseUrl() + (this.props.platform === 'Win' ? "/elauncher.php" : "/eview.php")}
                      onClick={() => this.props.screenChange('full')}
                      className="login-native-app-btn"
                    >
                      Enterprise Viewer
                    </a>
                  ) : (
                    <a
                      href={this.getServerBaseUrl() + (this.props.platform === 'Win' ? "/launcher.php" : "/dview.php")}
                      onClick={() => this.props.screenChange('full')}
                      className="login-native-app-btn"
                    >
                      Full Viewer
                    </a>
                  )
                )}
                {(this.props.platform === 'Ios' || this.props.platform === 'Mac') ? (
                  <a
                    href={this.props.platform === 'Mac'
                      ? "https://geo.itunes.apple.com/us/app/dividia-viewer/id1130011776?mt=12"
                      : "https://itunes.apple.com/us/app/dividia-viewer/id1143269725?mt=8"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="login-native-app-btn"
                  >
                    App Store
                  </a>
                ) : this.props.platform === 'Win' ? (
                  <a
                    href={this.getServerBaseUrl() + "/files/Launcher-1.2.4.0.exe"}
                    download="Launcher-1.2.4.0.exe"
                    className="login-native-app-btn"
                  >
                    ▼ Launcher
                  </a>
                ) : null}
              </div>
            )}

            <div className="login-footer">
              <div className="login-footer__row">
                {/* System (NVR/DVS) version of the server being LOGGED INTO — from the selected
                    server / Set-server resolution (state.displayVersion). This is the login TARGET's
                    version; the SERVING server's version is shown separately in "Served by …" below
                    (they can differ). The lite browser-app version surfaces via the "App Version"
                    line below once the build stamps REACT_APP_VERSION. #86bb7phhh (David 2026-08-17). */}
                {getProductConfig().hasServerBox && this.state.displayVersion && (
                  <p className="login-footer__text">Version {this.state.displayVersion}</p>
                )}
                <p className="login-footer__text">&copy;{currentYear} Dividia Technologies</p>
                {/* Removed the redundant "eview"/"full" footer link: it pointed to the SAME
                    eview.php/dview.php launcher as the "Enterprise Viewer"/"Full Viewer" button
                    (which also does screenChange('full')), so it was a bare, lower-contrast
                    duplicate. Native-viewer downloads now live in their own group below the
                    server box. (#86bb7phhh) */}
              </div>
            </div>
            </div>{/* end .login-bottom — downloads + footer */}

            {/* Show serving server info and app version */}
            {this.state.servingServer && (
              <div className="login-serving-info">
                <span className="login-serving-info__text">
                  Served by: {this.state.servingServer.sName} [{this.state.servingServer.bSerial}]
                  {serverVersionLabel(this.state.servingServer) ? ` ${versionTag(serverVersionLabel(this.state.servingServer))}` : ''}
                </span>
              </div>
            )}
            {/* dview-lite browser-app version — a small, always-visible corner tag, kept DISTINCT
                from the login "Version" (target NVR) and "Served by … v…" (serving NVR). #86bb7phhh */}
            <div className="login-app-version" aria-label="dview-lite app version">{APP_VERSION_LABEL}</div>
          </div>{/* end SECONDARY column */}

        </div>
      </div>
    );
  };
};

const mapStateToProps = state => {
    const { username, password, loginStatus, loginResult, loading, sessionEndReason } = state.auth;
    const { sServer, sName, sVersion, sReleaseVersion, fReleaseCandidate, serverUrl, bSerial, fEview } = state.server;
    const { platform } = state.utility;
    return {
        username,
        password,
        loginStatus,
        loginResult,
        loading,
        sessionEndReason,
        sServer,
        sName,
        sVersion,
        sReleaseVersion,
        fReleaseCandidate,
        serverUrl,
        bSerial,
        fEview,
        platform
    }
};

export default connect(
  mapStateToProps,
  {
    usernameChanged,
    passwordChanged,
    loginUser,
    loginCloud,
    changePasswordCloud,
    loginMicrosoft,
    loginCloudMicrosoft,
    loginToServer,
    screenChange,
    clearSessionModal
  })(Login);

// All styles moved to loginStyle.css
